import React, { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle,
  CardDescription,
  Button,
  Input,
  Textarea,
  Badge,
  OperatingHoursEditor,
  resolveOperatingHours,
  type OperatingHours,
} from '@besiktning/ui'
import {
  Building2,
  MapPin,
  Clock,
  Save,
  Upload,
  CalendarOff,
  Trash2,
} from 'lucide-react'
import { garageApi, carCareApi, formatPhone, uiLocaleTag, uiTimeZone, type BusinessProfile } from '@besiktning/ui'
import { useAuthStore } from '../store/authStore'
import { t } from '../i18n'


// Swedish kommuns for dropdown
const SWEDISH_KOMMUNS = [
  'Stockholm', 'Göteborg', 'Malmö', 'Uppsala', 'Linköping',
  'Västerås', 'Örebro', 'Norrköping', 'Helsingborg', 'Jönköping',
  'Umeå', 'Lund', 'Borås', 'Sundsvall', 'Gävle'
]

export default function Profile() {
  const { currentMembership, memberships } = useAuthStore()
  const isCarCare = currentMembership?.type === 'car_care'
  const isGarage = currentMembership?.type === 'garage'

  const currentRole = React.useMemo(() => {
    if (!currentMembership) return null
    return memberships.find(
      (m) => m.id === currentMembership.id && m.type === currentMembership.type
    )?.role ?? null
  }, [currentMembership, memberships])

  const canEdit =
    isCarCare || currentRole === 'owner' || currentRole === 'manager'
  const canManageClosures = isCarCare || isGarage

  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [newClosedDate, setNewClosedDate] = useState('')
  const logoInputRef = useRef<HTMLInputElement>(null)

  const { data: profile, isLoading } = useQuery({
    queryKey: ['profile', isCarCare ? 'car_care' : 'garage'],
    queryFn: () => isCarCare ? carCareApi.getProfile() : garageApi.getProfile(),
  })

  const [formData, setFormData] = useState<Partial<BusinessProfile>>({})

  // Update form data when profile loads
  React.useEffect(() => {
    if (profile) {
      setFormData({
        ...profile,
        operating_hours: resolveOperatingHours(profile.operating_hours),
      })
    }
  }, [profile])

  const operatingHours = resolveOperatingHours(formData.operating_hours)

  // Falls back to the old hardcoded horizon only if the profile payload is
  // still loading — the server is the source of truth for the default.
  const defaultHorizonDays = profile?.default_booking_horizon_days ?? 14

  const handleOperatingHoursChange = (hours: OperatingHours) => {
    setFormData((prev) => ({ ...prev, operating_hours: hours }))
  }

  const closuresApi = isCarCare ? carCareApi : garageApi

  const { data: closures = [], isLoading: closuresLoading } = useQuery({
    queryKey: ['closures', isCarCare ? 'car_care' : 'garage'],
    queryFn: () => closuresApi.getClosures(),
    enabled: canManageClosures,
  })

  const updateMutation = useMutation({
    mutationFn: (data: Partial<BusinessProfile>) => isCarCare ? carCareApi.updateProfile(data) : garageApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', isCarCare ? 'car_care' : 'garage'] })
      setIsEditing(false)
    },
    onError: () => {
      window.alert(t('profile.errSave'))
    },
  })

  const addClosureMutation = useMutation({
    mutationFn: (closed_on: string) => closuresApi.createClosure(closed_on),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['closures', isCarCare ? 'car_care' : 'garage'] })
      setNewClosedDate('')
    },
  })

  const deleteClosureMutation = useMutation({
    mutationFn: (id: number) => closuresApi.deleteClosure(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['closures', isCarCare ? 'car_care' : 'garage'] })
    },
  })

  const uploadLogoMutation = useMutation({
    mutationFn: (file: File) => isCarCare ? carCareApi.uploadLogo(file) : garageApi.uploadLogo(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['profile', isCarCare ? 'car_care' : 'garage'] })
    },
    onError: () => {
      window.alert(t('profile.errUploadLogo'))
    },
  })

  const handleLogoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      uploadLogoMutation.mutate(file)
    }
    e.target.value = ''
  }

  const handleSave = () => {
    updateMutation.mutate(formData)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    )
  }

  const profileLabel = isCarCare ? t('profile.carCareLabel') : t('profile.garageLabel')
  const profileDesc = isCarCare ? t('profile.carCareDesc') : t('profile.garageDesc')

  return (
    <div>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{profileLabel}</h1>
          <p className="text-gray-600">
            {profileDesc}
          </p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsEditing(false)
                    setFormData(profile ?? {})
                  }}
                >
                  {t('profile.cancel')}
                </Button>
                <Button
                  onClick={handleSave}
                  isLoading={updateMutation.isPending}
                >
                  <Save className="mr-2 h-4 w-4" />
                  {t('profile.save')}
                </Button>
              </>
            ) : (
              <Button onClick={() => setIsEditing(true)}>
                {t('profile.edit')}
              </Button>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Building2 className="mr-2 h-5 w-5" />
                {t('profile.basicInfo')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.garageName')}
                  </label>
                  {isEditing ? (
                    <Input
                      value={formData.name || ''}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.kommun')}
                  </label>
                  {isEditing ? (
                    <select
                      value={formData.kommun || ''}
                      onChange={(e) => setFormData({ ...formData, kommun: e.target.value })}
                      className="block w-full rounded-lg border-gray-300 border px-3 py-2"
                    >
                      <option value="">{t('profile.selectKommun')}</option>
                      {SWEDISH_KOMMUNS.map(kommun => (
                        <option key={kommun} value={kommun}>{kommun}</option>
                      ))}
                    </select>
                  ) : (
                    <p className="text-gray-900">{profile?.kommun}</p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.address')}
                  </label>
                  {isEditing ? (
                    <Input
                      value={formData.address || ''}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.address || t('profile.notProvided')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.email')}
                  </label>
                  {isEditing ? (
                    <Input
                      type="email"
                      value={formData.email || ''}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.email || t('profile.notProvided')}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.phone')}
                  </label>
                  {isEditing ? (
                    <Input
                      type="tel"
                      value={formData.phone || ''}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    />
                  ) : (
                    <p className="text-gray-900">
                      {profile?.phone ? formatPhone(profile.phone) : t('profile.notProvided')}
                    </p>
                  )}
                </div>

                {isGarage && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('profile.aftercheckPrice')}
                    </label>
                    {isEditing ? (
                      <Input
                        type="number"
                        min={0}
                        value={formData.aftercheck_price ?? ''}
                        onChange={(e) =>
                          setFormData({ ...formData, aftercheck_price: Number(e.target.value) })
                        }
                      />
                    ) : (
                      <p className="text-gray-900">{profile?.aftercheck_price ?? '—'}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-400">{t('profile.aftercheckPriceHint')}</p>
                  </div>
                )}

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('profile.description')}
                  </label>
                  {isEditing ? (
                    <Textarea
                      value={formData.description || ''}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={4}
                    />
                  ) : (
                    <p className="text-gray-900">{profile?.description || t('profile.noDescription')}</p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Operating Hours */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Clock className="mr-2 h-5 w-5" />
                {t('profile.openingHours')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <OperatingHoursEditor
                value={operatingHours}
                onChange={handleOperatingHoursChange}
                isEditing={isEditing && canEdit}
              />

              <div className="mt-6 border-t border-gray-100 pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('profile.bookingHorizon')}
                </label>
                {isEditing && canEdit ? (
                  <Input
                    type="number"
                    min={1}
                    max={365}
                    className="sm:max-w-xs"
                    placeholder={String(defaultHorizonDays)}
                    value={formData.booking_horizon_days ?? ''}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        // Empty means "inherit", which the API stores as NULL —
                        // not 0, which would leave the calendar with no dates.
                        booking_horizon_days: e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                ) : (
                  <p className="text-gray-900">
                    {profile?.booking_horizon_days ??
                      t('profile.bookingHorizonInherited', { days: defaultHorizonDays })}
                  </p>
                )}
                <p className="mt-1 text-xs text-gray-400">
                  {t('profile.bookingHorizonHint', { days: defaultHorizonDays })}
                </p>
              </div>
            </CardContent>
          </Card>

          {canManageClosures && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <CalendarOff className="mr-2 h-5 w-5" />
                  {t('profile.closedDays')}
                </CardTitle>
                <CardDescription>
                  {t('profile.closedDaysDesc')}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {closuresLoading ? (
                  <div className="flex justify-center py-6">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                  </div>
                ) : (
                  <>
                    <div className="flex flex-col sm:flex-row gap-2 mb-4">
                      <Input
                        type="date"
                        value={newClosedDate}
                        min={new Date().toISOString().slice(0, 10)}
                        onChange={(e) => setNewClosedDate(e.target.value)}
                        className="sm:max-w-xs"
                      />
                      <Button
                        type="button"
                        disabled={!newClosedDate || addClosureMutation.isPending}
                        isLoading={addClosureMutation.isPending}
                        onClick={() => newClosedDate && addClosureMutation.mutate(newClosedDate)}
                      >
                        {t('profile.addClosedDay')}
                      </Button>
                    </div>
                    {closures.length === 0 ? (
                      <p className="text-sm text-gray-500">{t('profile.noClosedDays')}</p>
                    ) : (
                      <ul className="divide-y divide-gray-100">
                        {closures.map((closure) => (
                          <li
                            key={closure.id}
                            className="flex items-center justify-between py-3"
                          >
                            <span className="text-gray-900">
                              {new Date(closure.closed_on).toLocaleDateString(uiLocaleTag(), {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                timeZone: uiTimeZone(),
                              })}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteClosureMutation.mutate(closure.id)}
                              disabled={deleteClosureMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4 text-red-600" />
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Logo Upload */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.logo')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center">
                <div className="h-32 w-32 rounded-lg bg-gray-100 flex items-center justify-center mb-4 overflow-hidden">
                  {profile?.logo_url ? (
                    <img
                      src={profile.logo_url}
                      alt={t('profile.logo')}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <Building2 className="h-16 w-16 text-gray-400" />
                  )}
                </div>
                {canEdit && (
                  <>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleLogoSelected}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      isLoading={uploadLogoMutation.isPending}
                      onClick={() => logoInputRef.current?.click()}
                    >
                      <Upload className="mr-2 h-4 w-4" />
                      {t('profile.uploadImage')}
                    </Button>
                  </>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Stats */}
          <Card>
            <CardHeader>
              <CardTitle>{t('profile.statistics')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('profile.rating')}</span>
                  <div className="flex items-center">
                    <span className="font-medium mr-1">{profile?.rating || 0}</span>
                    <span className="text-yellow-500">★</span>
                  </div>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('profile.reviews')}</span>
                  <span className="font-medium">{profile?.review_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t('profile.status')}</span>
                  <Badge variant={profile?.active ? 'success' : 'default'}>
                    {profile?.active ? t('profile.active') : t('profile.inactive')}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Service Areas */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <MapPin className="mr-2 h-5 w-5" />
                {t('profile.serviceAreas')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                <Badge>{profile?.kommun}</Badge>
                <Badge variant="default">{t('profile.nearbyKommuns')}</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}
